import { useTranslation } from "react-i18next";
import {
  HelpCircle,
  Search,
  Menu,
  LogOut,
  User,
  Languages,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationsBell } from "@/features/notifications/components/NotificationsBell";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuthStore } from "@/features/auth/stores/authStore";
import i18n from "@/lib/i18n";
import { useNavigate } from "react-router-dom";

export function Topbar({
  onMenuClick,
  sidebarHidden = false,
  onToggleSidebar,
}: {
  onMenuClick?: () => void;
  sidebarHidden?: boolean;
  onToggleSidebar?: () => void;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  function handleLogout() {
    // logout() revoga o cookie HttpOnly `rt` no backend (no-op em mock) e limpa o
    // estado local independentemente do resultado da chamada.
    void logout();
    navigate("/login", { replace: true });
  }

  function switchLang() {
    const next = i18n.resolvedLanguage === "pt-BR" ? "en-US" : "pt-BR";
    void i18n.changeLanguage(next);
  }

  return (
    <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b border-border bg-white px-3 lg:px-4">
      <button
        type="button"
        onClick={onMenuClick}
        className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded-lg text-ink-muted hover:bg-bg lg:hidden"
        aria-label="Abrir menu"
      >
        <Menu size={20} />
      </button>

      {/* Toggle desktop — mostra/esconde sidebar (Argon-style) */}
      {onToggleSidebar && (
        <button
          type="button"
          onClick={onToggleSidebar}
          className="-ml-1 hidden h-7 w-7 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-bg hover:text-ink lg:inline-flex"
          aria-label={sidebarHidden ? "Mostrar menu lateral" : "Esconder menu lateral"}
          title={sidebarHidden ? "Mostrar menu" : "Esconder menu"}
        >
          {sidebarHidden ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
      )}

      <div className="hidden flex-1 items-center gap-2 rounded-xl border border-border px-3 py-2 text-ink-muted md:flex md:max-w-md">
        <Search size={16} />
        <input
          type="search"
          placeholder={t("topbar.search")}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-soft"
        />
      </div>

      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={switchLang}
          className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs font-semibold text-ink-muted hover:border-ink/30 hover:text-ink"
          aria-label="Trocar idioma"
        >
          <Languages size={14} />
          {i18n.resolvedLanguage === "pt-BR" ? "PT" : "EN"}
        </button>

        <button
          type="button"
          className="relative inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border text-ink-muted hover:border-ink/30 hover:text-ink"
          aria-label="Ajuda"
        >
          <HelpCircle size={18} />
        </button>

        <NotificationsBell />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="ml-1 inline-flex items-center gap-2 rounded-full border border-border bg-white py-1 pl-1 pr-3 text-left hover:border-ink/30"
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback>
                  {(user?.name ?? "??").slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-[13px] font-medium md:inline">
                {user?.name ?? "—"}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{user?.email ?? ""}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/settings")}>
              <User size={16} />
              {t("topbar.profile")}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate("/settings")}>
              <Settings size={16} />
              Configurações
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleLogout} className="text-danger">
              <LogOut size={16} />
              {t("topbar.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
